import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Brush, UploadCloud } from 'lucide-react';
import type { JawsCorrectionResponse } from '@/schema/JawsCorrection';
import { WeekSelector } from '@export/UI/WeekSelector.tsx';
import { AlignerPanel } from '@export/UI/AlignerPanel.tsx';
import { TrimThickeningPanel } from '@export/UI/TrimThickeningPanel.tsx';
import { AlignerGenerationOverlay } from '@export/UI/AlignerGenerationOverlay.tsx';
import { SegmentationWorkflowOverlay } from '@segmentation/UI/SegmentationWorkflowOverlay';
import useExportStore from '@export/hooks/useExportStore';
import { Button, IconButton } from '@/components/ui/button';
import DeclarationApi from '@/api/declarationApi';

interface Props {
  corrections: JawsCorrectionResponse[];
}

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

export function ExportUI({ corrections }: Readonly<Props>) {
  const {
    generateAlignerShell,
    alignerStatus,
    trimCurveState,
    trimThickeningActive,
    setTrimThickeningActive,
    scanId,
  } = useExportStore();
  const navigate = useNavigate();

  const [uploadState, setUploadState] = useState<UploadState>('idle');

  const downloadDeclaration = async () => {
    if (!scanId) return;
    const blob = await DeclarationApi.getScanDeclarationPdf(scanId);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `prohlaseni-${scanId}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const handleUploadBraces = async () => {
    if (!scanId || uploadState === 'uploading') return;
    setUploadState('uploading');
    try {
      // Fake the "uploading finished braces to the server" step for now.
      await new Promise((resolve) => setTimeout(resolve, 1500));
      // On success, hand the declaration PDF to the user…
      await downloadDeclaration();
      setUploadState('success');
      // …then take them one step back to the main page.
      setTimeout(() => navigate(-1), 1500);
    } catch {
      setUploadState('error');
    }
  };

  return (
    <div className="pointer-events-none absolute inset-0">
      {/* Right: week selector + collapsible aligner panel + thickening toggle. */}
      <div className="pointer-events-auto absolute right-4 top-4 z-20 flex w-full max-w-[320px] flex-col gap-3">
        <WeekSelector corrections={corrections} />
        <AlignerPanel generateAlignerShell={generateAlignerShell} status={alignerStatus} />
        {trimCurveState === 'ready' && (
          <Button
            type="button"
            variant={trimThickeningActive ? 'default' : 'outline-white'}
            className="w-full"
            onClick={() => setTrimThickeningActive(!trimThickeningActive)}
          >
            <Brush />
            {trimThickeningActive ? 'Thickening active' : 'Thickening'}
          </Button>
        )}
        <Button
          type="button"
          variant="default"
          className="w-full"
          disabled={!scanId || uploadState === 'uploading'}
          onClick={handleUploadBraces}
        >
          <UploadCloud />
          Upload braces
        </Button>
      </div>

      {/* Left-middle: thickening options (only while the brush is active). */}
      <TrimThickeningPanel />

      {/* Full-screen loading overlay while a shell is generating. */}
      <AlignerGenerationOverlay />

      {/* Fake braces-upload workflow: loading → success (downloads PDF + goes back). */}
      <SegmentationWorkflowOverlay
        open={uploadState === 'uploading'}
        eyebrow="Uploading braces"
        title="Uploading braces to the server"
        message="Finalizing your case"
        variant="loading"
      />
      <SegmentationWorkflowOverlay
        open={uploadState === 'success'}
        title="Upload successful"
        message="Declaration downloaded. Returning back in a moment…"
        variant="success"
      />
      <SegmentationWorkflowOverlay
        open={uploadState === 'error'}
        title="Upload failed"
        message="Something went wrong while finalizing. Try again."
        variant="error"
        footer={
          <IconButton
            className="mt-2 w-full rounded-2xl border border-red-400/40 bg-red-500/20"
            variant="ghost-danger"
            icon={ArrowLeft}
            onClick={() => setUploadState('idle')}
          >
            Back
          </IconButton>
        }
      />
    </div>
  );
}
